import type { Middleware } from "@reduxjs/toolkit";
import {
    SHOW_STORYLINE,
    GO_TO_X_Y_Z,
    GO_TO_ID,
    DATASOURCE_UPDATED,
    UPDATE_CURRENT_FRAME_DATA,
    UPDATE_CURRENT_SLIDE_TEMPLATE,
    UPDATE_SLIDE_TEMPLATES,
} from "../storyline/types";
import { ADD_TRACE_EVENT, TRACING_SET_ENABLED, TRACING_CLEAR, TraceSource } from "./types";
import { addTraceEvent } from "./actions";

// Actions this middleware never inspects (its own trace actions can't change frame data — skip the work).
const IGNORED_ACTION_TYPES = new Set<string>([ADD_TRACE_EVENT, TRACING_SET_ENABLED, TRACING_CLEAR]);

/** Attribute a frame-data change to a source based on the action that produced it. */
function frameDataSource(action: any): TraceSource {
    switch (action?.type) {
        case DATASOURCE_UPDATED:
            return { kind: "DatasourceBinding", datasourceName: action.datasourceName };
        case UPDATE_CURRENT_FRAME_DATA:
            return { kind: "ManualUpdate" };
        case SHOW_STORYLINE:
        case GO_TO_X_Y_Z:
        case GO_TO_ID:
            return { kind: "PageNavigation" };
        default:
            return { kind: "ManualUpdate" };
    }
}

/** The template names a template-push action targets (empty for any other action). */
function targetedTemplateNames(action: any, state: any): string[] {
    switch (action?.type) {
        case UPDATE_SLIDE_TEMPLATES:
            return Object.keys(action.newTemplates ?? {});
        case UPDATE_CURRENT_SLIDE_TEMPLATE:
            return [state.storyline?.currentParagraph?.templateName].filter(Boolean);
        default:
            return [];
    }
}

/**
 * Records traces for *derived* storyline values that change as a side effect of a reducer rather than via
 * a dedicated thunk, and which drive Canvas re-renders:
 *
 * - `FrameDataChanged` — `storyline.currentParagraph.data` (recomputed inside the `DATASOURCE_UPDATED` /
 *   `SHOW_STORYLINE` reducer cases via `getFrame`, or set by `UPDATE_CURRENT_FRAME_DATA`).
 * - `DatasourcesInFlightChanged` — `storyline.datasourcesInFlight` (the in-flight datasource Set, updated
 *   by `UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE`; passed to Canvas as a prop, so a change triggers a render).
 * - `TemplateUpdated` — a template push from the Dev Tools window or the VS Code extension (the
 *   `UPDATE_CURRENT_SLIDE_TEMPLATE` / `UPDATE_SLIDE_TEMPLATES` reducer cases bump the template's `version`
 *   only when jsx/css actually changed, so a version delta is exactly a remount-causing update).
 *
 * Each reducer produces a fresh reference only on a real change, so an O(1) reference compare across
 * `next(action)` catches every change without deep-diffing.  Gated on `tracing.enabled` so it is near-zero
 * overhead when off.
 */
export const traceStorylineMiddleware: Middleware = store => next => action => {
    const state = store.getState();
    if (!state.tracing?.enabled || IGNORED_ACTION_TYPES.has((action as any)?.type)) {
        return next(action);
    }

    const prevData = state.storyline?.currentParagraph?.data;
    const prevInFlight = state.storyline?.datasourcesInFlight;
    // Capture the versions as primitives before the reducer runs — UPDATE_CURRENT_SLIDE_TEMPLATE mutates
    // the (possibly aliased) template object in place, so the pre-dispatch object can't be trusted afterwards.
    const templateNames = targetedTemplateNames(action, state);
    const prevTemplateVersions = templateNames.map(name => state.storyline?.storyline?.templates?.[name]?.version ?? 0);

    const result = next(action);

    const nextState = store.getState();
    const nextData = nextState.storyline?.currentParagraph?.data;
    const nextInFlight = nextState.storyline?.datasourcesInFlight;

    if (prevData !== nextData) {
        store.dispatch(
            addTraceEvent({
                kind: "FrameDataChanged",
                oldValue: prevData,
                newValue: nextData,
                source: frameDataSource(action),
            }) as any
        );
    }

    if (prevInFlight !== nextInFlight) {
        // Only `UPDATE_INFLIGHT_REQUESTS_FOR_DATASOURCE` changes this Set; it carries the datasource whose
        // in-flight status just flipped.  A datasource entering the set is a request going out; leaving it is
        // its response arriving.  Serialize the Sets to arrays for legible old/new display.
        const datasourceName = (action as any)?.datasource?.name;
        const entered = !!datasourceName && !!nextInFlight?.has(datasourceName);
        store.dispatch(
            addTraceEvent({
                kind: "DatasourcesInFlightChanged",
                name: datasourceName,
                oldValue: prevInFlight ? [...prevInFlight] : [],
                newValue: nextInFlight ? [...nextInFlight] : [],
                source: { kind: entered ? "DatasourceRequest" : "DatasourceResponse" },
            }) as any
        );
    }

    templateNames.forEach((name, i) => {
        const oldVersion = prevTemplateVersions[i];
        const newVersion = nextState.storyline?.storyline?.templates?.[name]?.version ?? 0;
        if (oldVersion === newVersion) return; // no version bump => content unchanged => no remount

        store.dispatch(
            addTraceEvent({
                kind: "TemplateUpdated",
                name,
                oldValue: `v${oldVersion}`,
                newValue: `v${newVersion}`,
                source: { kind: (action as any)?.origin === "VsCodeExtension" ? "VsCodeExtension" : "DevTools" },
            }) as any
        );
    });

    return result;
};
